import swal from "sweetalert";
import { getThemeColor, DEFAULT_THEME_COLOR } from "../utils/themeUtils";

/**
 * Resolve the active BackOffice theme color (hex) from localStorage / defaults.
 */
export function resolveAppThemeColor() {
  try {
    const raw = localStorage.getItem("appSettings");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.appTheme) {
        return getThemeColor(parsed.appTheme);
      }
    }
  } catch (_) {
    // fall through
  }
  return DEFAULT_THEME_COLOR;
}

function applyUnsavedSwalTheme(themeColor) {
  const modal = document.querySelector(".swal-modal.swal-modal--unsaved-changes");
  if (!modal) return false;

  modal.style.setProperty("--swal-theme-color", themeColor);

  const saveBtn = modal.querySelector(".swal-btn--save");
  if (saveBtn) {
    saveBtn.style.backgroundColor = themeColor;
    saveBtn.style.backgroundImage = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
    saveBtn.style.borderColor = themeColor;
    saveBtn.style.color = "#fff";
    saveBtn.style.boxShadow = `0 4px 15px ${themeColor}59`;
  }

  const discardBtn = modal.querySelector(".swal-btn--discard");
  if (discardBtn) {
    discardBtn.style.backgroundColor = "transparent";
    discardBtn.style.border = `1.5px solid ${themeColor}`;
    discardBtn.style.color = themeColor;
  }

  const warningIcon = modal.querySelector(".swal-icon--warning");
  if (warningIcon) {
    warningIcon.style.borderColor = themeColor;
    warningIcon
      .querySelectorAll(".swal-icon--warning__body, .swal-icon--warning__dot")
      .forEach((el) => {
        el.style.backgroundColor = themeColor;
      });
  }

  return true;
}

function scheduleUnsavedSwalTheme(themeColor) {
  let tries = 0;
  const id = window.setInterval(() => {
    tries += 1;
    if (applyUnsavedSwalTheme(themeColor) || tries > 40) {
      window.clearInterval(id);
    }
  }, 16);
}

/**
 * Themed SweetAlert for unsaved-changes: Cancel · Don't Save · Save
 * Matches CustomModal button hierarchy (neutral / outline / theme primary).
 *
 * @param {object} [options]
 * @param {string} [options.title]
 * @param {string} [options.text]
 * @returns {Promise<'save'|'discard'|null>}
 */
export default async function confirmUnsavedChangesSwal({
  title = "Unsaved changes",
  text = "You have unsaved changes. Do you want to save before leaving?",
} = {}) {
  const themeColor = resolveAppThemeColor();

  document.documentElement.style.setProperty("--theme-color", themeColor);
  document.documentElement.style.setProperty("--swal-theme-color", themeColor);
  scheduleUnsavedSwalTheme(themeColor);

  const result = await swal({
    title,
    text,
    icon: "warning",
    className: "swal-modal--unsaved-changes",
    buttons: {
      cancel: {
        text: "Cancel",
        value: null,
        visible: true,
        className: "swal-btn swal-btn--cancel",
        closeModal: true,
      },
      discard: {
        text: "Don't Save",
        value: "discard",
        visible: true,
        className: "swal-btn swal-btn--discard",
        closeModal: true,
      },
      save: {
        text: "Save",
        value: "save",
        visible: true,
        className: "swal-btn swal-btn--save",
        closeModal: true,
      },
    },
    closeOnClickOutside: false,
    closeOnEsc: true,
  });

  if (result === "save" || result === "discard") {
    return result;
  }
  return null;
}
